import { Box, Container, Heading, Text, VStack, Grid } from "@chakra-ui/react";
import { Helmet } from 'react-helmet-async';
import ServiceDetailsHeader from "../../components/Common/ImageHeader";
import ServiceCard from "../../components/Common/ServiceCard";
import BenefitCard from "../../components/Common/BenefitCard";
import FeatureCard from "../../components/Common/FeatureCard";
import CoCreationSection from "../../components/Common/CoCreationSection";
import ExploreSection from "../../components/ProductDetails/ExploreSection";

const CustomizedSoftwareDevelopmentPage = () => {
  // Tools & Technologies Data
  const toolsAndTechnologies = [
    {
      id: 1,
      title: "Web Development Frameworks",
      description:
        "Laravel, CakePHP, Django, ASP.NET Core, Spring Boot, Node.js",
      icon: "/svg/web_development.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 2,
      title: "Frontend Technologies",
      description:
        "React.js, Vue.js, Angular, Bootstrap, Tailwind CSS, HTML5, CSS3",
      icon: "/svg/Frontend_Technologies.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 3,
      title: "Mobile Application Development",
      description: "Flutter, React Native, Kotlin, Swift",
      icon: "/svg/Mobile_Application_Development.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 4,
      title: "Cloud Platforms",
      description:
        "Amazon Web Services (AWS), Microsoft Azure, Google Cloud Platform (GCP), Bangladesh National Data Center",
      icon: "/svg/Cloud.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 5,
      title: "Database Technologies",
      description:
        "PostgreSQL, MySQL, Oracle DB, Microsoft SQL Server, MongoDB",
      icon: "/svg/Database.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 6,
      title: "Data Analytics & Visualization",
      description: "Power BI, Apache Superset, Grafana, Tableau, Kibana",
      icon: "/svg/Data_Analytics.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 7,
      title: "DevOps & CI/CD Tools",
      description:
        "Docker, Kubernetes, Jenkins, GitLab CI, GitHub Actions, Ansible, Terraform",
      icon: "/svg/DevOps_Services.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 8,
      title: "Monitoring & Logging",
      description:
        "Prometheus, ELK Stack (Elasticsearch, Logstash, Kibana), Azure Monitor",
      icon: "/svg/Monitoring.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 9,
      title: "Cyber Security Tools",
      description:
        "OWASP ZAP, Nessus, FortiGate Firewall, Web Application Firewall (WAF)",
      icon: "/svg/Cyber_Security.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 10,
      title: "AI & Machine Learning Tools",
      description:
        "Python (Pandas, Scikit-learn, TensorFlow), R, OpenAI API, Natural Language Processing (NLP) engines",
      icon: "/svg/AI.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 11,
      title: "GIS & Geo-Analytics",
      description: "ArcGIS, Leaflet.js, Mapbox, QGIS",
      icon: "/svg/GIS.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 12,
      title: "Communication & Support Platforms",
      description:
        "Jira, Confluence, Microsoft Teams, Slack, Freshdesk, Custom Helpdesk Systems",
      icon: "/svg/Support.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
  ];

  // What We Build Data
  const whatWeBuild = [
    {
      id: 1,
      title: "e-Government Platforms",
      description:
        "Licensing, service delivery, inspections, document processing",
    },
    {
      id: 2,
      title: "Enterprise Solutions",
      description: "Custom CRM, project management, HR & ticketing systems",
    },
    {
      id: 3,
      title: "Regulatory & Compliance Systems",
      description: "Permitting, audit trails, automated alerts",
    },
    {
      id: 4,
      title: "Aid & Donor Program Systems",
      description:
        "Grant lifecycle, KPI dashboards, multi-stakeholder workflows",
    },
    {
      id: 5,
      title: "Citizen Service Portals",
      description: "Inclusive, multilingual, AI-assisted service journeys",
    },
    {
      id: 6,
      title: "Mobile Apps & Smart Dashboards",
      description: "Real-time data visualizations, user feedback loops",
    },
  ];

  // Co-Creation Processes Data
  const coCreationProcesses = [
    {
      id: 1,
      title: "Design thinking workshops & rapid prototyping",
      icon: "/svg/Design_Thinking.svg",
    },
    {
      id: 2,
      title: "Stakeholder mapping and user journey mapping",
      icon: "/svg/Stakeholder.svg",
    },
    {
      id: 3,
      title: "Feedback loops with government & development sector end users",
      icon: "/svg/Feedback.svg",
    },
    {
      id: 4,
      title: "Joint validation and UAT testing with clients",
      icon: "/svg/Validation.svg",
    },
    {
      id: 5,
      title: "Change management & documentation",
      icon: "/svg/Change_Management.svg",
    },
  ];

  // Key Features Data for Custom App Development
  const keyFeatures = [
    {
      id: 1,
      title: "AI-Powered DevOps",
      description: "From code suggestion to performance monitoring",
    },
    {
      id: 2,
      title: "Agile, Transparent Delivery",
      description: "Weekly demos, Git-based repos, CI/CD pipelines",
    },
    {
      id: 3,
      title: "Inclusive Design",
      description: "Bangla-English, WCAG compliance, mobile-first UX",
    },
    {
      id: 4,
      title: "Modular Architecture",
      description: "Future-ready components for expansion",
    },
    {
      id: 5,
      title: "Enterprise-Grade Security",
      description: "OAuth2, 2FA, encrypted data layers",
    },
    {
      id: 6,
      title: "3rd-Party Integrations",
      description: "Payment gateways, SMS APIs, identity verification",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Customized Software Development | AI-Driven, Co-Created Enterprise & eGov Platforms – Bangladesh</title>
        <meta 
          name="description" 
          content="Business Automation Ltd. offers AI-powered, custom software development for governments, enterprises, and donor-funded projects in Bangladesh—built through agile, co-creation models. Smarter solutions, faster delivery, lasting impact." 
        />
        <meta 
          name="keywords" 
          content="Custom software development Bangladesh, AI-powered software solutions, government software development, enterprise application development, donor-funded software projects, agile development Bangladesh, co-creation software development, digital transformation Bangladesh, scalable software solutions, bespoke software development" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/services/customized-software-development" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/services/customized-software-development" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Customized Software Development | AI-Driven, Co-Created Enterprise & eGov Platforms – Bangladesh" />
        <meta property="og:description" content="Business Automation Ltd. offers AI-powered, custom software development for governments, enterprises, and donor-funded projects in Bangladesh—built through agile, co-creation models. Smarter solutions, faster delivery, lasting impact." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/services/customized-software-development" />
        <meta name="twitter:title" content="Customized Software Development | AI-Driven, Co-Created Enterprise & eGov Platforms – Bangladesh" />
        <meta name="twitter:description" content="Business Automation Ltd. offers AI-powered, custom software development for governments, enterprises, and donor-funded projects in Bangladesh—built through agile, co-creation models. Smarter solutions, faster delivery, lasting impact." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <ServiceDetailsHeader
        title="Customized Software Development"
        subtitle="Tailored Software Solutions for Your Business Needs"
        backgroundImage="/png/bg_sd.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/our-services" },
          {
            label: "Customized Software Development",
            href: "#",
            isCurrentPage: true,
          },
        ]}
      />

      <Box
        as="section"
        py={{ base: 4, md: 6 }}
        sx={{
          backgroundImage: "url('/png/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
        }}
      >
        <Box py={{ base: "30px", md: "40px" }}>
          <Container maxW="7xl">
            <VStack spacing={8} align="start">
              {/* Header Section */}
              <VStack spacing={4} align="start" w="full">
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  lineHeight="1.5"
                  textAlign="justify"
                >
                  At Business Automation Limited, we don’t just develop software—we
                  co-create intelligent digital systems that are designed to
                  evolve. Our customized software development process is driven
                  by AI, shaped by deep domain expertise, and guided by
                  user-centric design.
                </Text>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  lineHeight="1.5"
                  textAlign="justify"
                >
                  For over two decades, we’ve helped governments, enterprises,
                  and donor-backed programs unlock transformation through
                  tailored digital solutions—from citizen service portals and
                  regulatory platforms to workflow automation and enterprise
                  systems. And now, with our strategic investments in AI, NLP,
                  and RPA, we’re redefining how technology is developed,
                  deployed, and supported in Bangladesh and beyond.
                </Text>
              </VStack>
              <Heading
                pt={8}
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color="gray.800"
              >
                How We Use AI{" "}
                <Text as="span" color="#000B8C">
                  in Our Development Lifecycle
                </Text>
              </Heading>
              <Box w="full" display="flex" justifyContent="center" py={8}>
                <Box
                  as="img"
                  src="/png/software.png"
                  alt="AI-driven cybersecurity capabilities"
                  maxW="100%"
                  height="auto"
                  borderRadius="lg"
                  loading="lazy"
                  draggable="false"
                />
              </Box>

              <Box w="full" py={8}>
                <VStack spacing={8} align="start">
                  <Heading
                    fontSize={{ base: "2xl", md: "3xl" }}
                    fontWeight="bold"
                    color="gray.800"
                  >
                    Tools &{" "}
                    <Text as="span" color="#000B8C">
                      Technologies
                    </Text>
                  </Heading>

                  <Grid
                    templateColumns={{
                      base: "1fr",
                      md: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    }}
                    gap={6}
                    w="full"
                  >
                    {toolsAndTechnologies.map((tech) => (
                      <ServiceCard key={tech.id} service={tech} />
                    ))}
                  </Grid>
                </VStack>
              </Box>
              <Box py={{ base: "30px", md: "40px" }}>
                <Container maxW="7xl">
                  <VStack spacing={8} align="start">
                    <Heading
                      pt={8}
                      fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                      fontWeight="bold"
                      color="gray.800"
                    >
                      Technology{" "}
                      <Text as="span" color="#000B8C">
                        Stack
                      </Text>
                    </Heading>
                    <Box w="full" display="flex" justifyContent="center" py={8}>
                      <Box
                        as="img"
                        src="/png/stack.png"
                        alt="AI-driven cybersecurity capabilities"
                        maxW="100%"
                        height="auto"
                        borderRadius="lg"
                        loading="lazy"
                        draggable="false"
                      />
                    </Box>
                    <Box w="full" py={8}>
                      <VStack spacing={8} align="start">
                        <Heading
                          fontSize={{ base: "2xl", md: "3xl" }}
                          fontWeight="bold"
                          color="gray.800"
                        >
                          What We{" "}
                          <Text as="span" color="#000B8C">
                            Build
                          </Text>
                        </Heading>

                        <Grid
                          templateColumns={{
                            base: "1fr",
                            md: "repeat(2, 1fr)",
                          }}
                          gap={6}
                          w="full"
                        >
                          {whatWeBuild.map((item) => (
                            <BenefitCard key={item.id} benefit={item} />
                          ))}
                        </Grid>
                      </VStack>
                    </Box>
                  </VStack>
                </Container>
                <FeatureCard
                  title="Key Features"
                  features={keyFeatures}
                  backgroundImage="/png/share-your-idea.png"
                />
                <CoCreationSection
                  blueTitle={"Co-Creation: How"}
                  blackTitle={" We Work With You"}
                  title="Co-Creation: How We Work With You"
                  subtitle="Every successful project starts with deep collaboration. We engage clients through:"
                  processes={coCreationProcesses}
                  illustrationImage="/svg/CO-CREATION.svg"
                  footerText="This co-creation ensures ownership, usability, and long-term sustainability."
                />
                <ExploreSection />
              </Box>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default CustomizedSoftwareDevelopmentPage;
